import React, { useRef, useState } from "react";
import { motion, useInView, AnimatePresence } from "framer-motion";
import styles from "./PainPoint.module.scss";

const painPoints = [
  {
    id: "scattered",
    step: "01",
    title: "The job is scattered",
    summary: "Reps lose hours jumping between systems — not because they can't sell.",
    description:
      "The modern sales job is spread across inboxes, CRMs, calendars, meeting notes, tasks, and follow-up reminders.",
    chips: ["Inbox", "CRM", "Calendar", "Notes", "Tasks", "Follow-ups"],
  },
  {
    id: "slippage",
    step: "02",
    title: "Deals slip quietly",
    summary: "No dramatic moment — just small misses that compound.",
    description:
      "One missed reply, one stale CRM field, one unprepared meeting, one forgotten stakeholder at a time.",
    chips: [
      "Missed reply",
      "Stale CRM",
      "Unprepared meeting",
      "Forgotten stakeholder",
    ],
  },
  {
    id: "overload",
    step: "03",
    title: "The stack manages you",
    summary: "Every tool meant to help adds another place to check.",
    description:
      "Another tab to open, another place the truth might be hiding. The stack meant to help you sell now needs managing itself.",
    chips: ["Another tab", "Hidden truth", "Tool overload"],
  },
];

const PainPoint = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.15 });
  const [activeId, setActiveId] = useState("scattered");

  const activePoint =
    painPoints.find((point) => point.id === activeId) ?? painPoints[0];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.08 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 28 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.55, ease: "easeOut" },
    },
  };

  return (
    <motion.section
      id="pain-point"
      ref={ref}
      className={styles.section}
      variants={containerVariants}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
    >
      <div className="container-custom">
        <div className={styles.sectionShell} data-section-shell="true">
          <motion.span className={styles.eyebrow} variants={itemVariants}>
            The Problem
          </motion.span>

          <motion.h2 className={styles.headline} variants={itemVariants}>
            Sales teams don&apos;t need another tool.
            <span className={styles.headlineAccent}>
              They need their day back.
            </span>
          </motion.h2>

          <motion.p className={styles.lead} variants={itemVariants}>
            Your best reps aren&apos;t losing time because they can&apos;t sell.
            They&apos;re losing it to a workflow that was never designed to stay
            in one place.
          </motion.p>

          <motion.div className={styles.cardsGrid} variants={itemVariants}>
            {painPoints.map((point) => {
              const isActive = activeId === point.id;

              return (
                <motion.button
                  key={point.id}
                  type="button"
                  className={`${styles.card} ${isActive ? styles.cardActive : ""}`}
                  onClick={() => setActiveId(point.id)}
                  whileHover={{ y: -4 }}
                  whileTap={{ scale: 0.98 }}
                  aria-pressed={isActive}
                >
                  <span className={styles.cardStep}>{point.step}</span>
                  <h3 className={styles.cardTitle}>{point.title}</h3>
                  <p className={styles.cardSummary}>{point.summary}</p>
                  <span className={styles.cardHint}>
                    {isActive ? "Selected" : "Tap to explore"}
                  </span>
                </motion.button>
              );
            })}
          </motion.div>

          <motion.div className={styles.detailPanel} variants={itemVariants}>
            <AnimatePresence mode="wait">
              <motion.div
                key={activePoint.id}
                className={styles.detailContent}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: 0.35, ease: "easeOut" }}
              >
                <div className={styles.detailHeader}>
                  <span className={styles.detailStep}>{activePoint.step}</span>
                  <h3 className={styles.detailTitle}>{activePoint.title}</h3>
                </div>

                <p className={styles.detailDescription}>
                  {activePoint.description}
                </p>

                <div className={styles.chipRow}>
                  {activePoint.chips.map((chip, index) => (
                    <motion.span
                      key={chip}
                      className={styles.chip}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: index * 0.05, duration: 0.25 }}
                    >
                      {chip}
                    </motion.span>
                  ))}
                </div>
              </motion.div>
            </AnimatePresence>
          </motion.div>

          <motion.div className={styles.solutionBanner} variants={itemVariants}>
            <div className={styles.solutionIcon} aria-hidden="true">
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M10 2L11.8 7.2L17 8L11.8 8.8L10 14L8.2 8.8L3 8L8.2 7.2L10 2Z"
                  fill="currentColor"
                />
              </svg>
            </div>
            <p className={styles.solutionText}>
              <strong>Vertik carries that whole job for them</strong> — a Chief
              of Staff inside the work, so reps get back to selling.
            </p>
          </motion.div>
        </div>
      </div>
    </motion.section>
  );
};

export default PainPoint;
